// frontend/src/components/layout/mobile/MoreSheet.jsx
//
// The More tab: every screen the tab bar has no slot for (mobileNav.moreMenu).
// The sidebar's screens come first, then the tools, then the account — the
// store and the user, each a card that opens its profile — then help, and
// log out last.

import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ICON, CaretRight, Power, Wrench } from '../../../icons';
import { useAuth } from '../../../contexts/AuthContext';
import { useInAppSyncStatus } from '../../../hooks/useInAppSyncStatus';
import { trackEvent } from '../../../hooks/useTracking';
import { forecastsMissing } from '../../../utils/syncGate';
import { storeDisplayName, storeDomainIsDistinct, userDisplayName, userEmailIsDistinct } from '../../../utils/avatar';
import { HELP_CENTER_URL } from '../../../config/helpCenter';
import TabLink from '../../../tabs/TabLink';
import { isNavPathActive } from '../navActive';
import { moreMenu } from './mobileNav';
import UserChip from '../UserChip';
import StoreChip from '../StoreChip';
import BottomSheet from './BottomSheet';

// Routes through TabLink (focus-or-create), like every opener outside the tab
// strip: a raw <Link> to the screen already shown would open nothing.
function PageRow({ to, onNavigate, children }) {
  const { pathname } = useLocation();
  const active = isNavPathActive(to, pathname);
  return (
    <TabLink
      to={to}
      className={`sheet-row${active ? ' sheet-row--active' : ''}`}
      aria-current={active ? 'page' : undefined}
      onClick={onNavigate}
    >
      {children}
      <span className="sheet-row__trail"><CaretRight size={ICON.md} /></span>
    </TabLink>
  );
}

// One page row per link. Staff tools carry the wrench the desktop user menu
// marks them with.
function LinkRows({ links, onNavigate }) {
  const { t } = useTranslation();
  return links.map((link) => (
    <li key={link.path}>
      <PageRow to={link.path} onNavigate={onNavigate}>
        <span className="sheet-row__text">{t(link.labelKey)}</span>
        {link.staffOnly && <Wrench size={ICON.md} className="sheet-row__staff" />}
      </PageRow>
    </li>
  ));
}

// A store's or a user's card: their chip, their name and, when it says
// something more, their address.
function IdentityRow({ to, chip, name, detail, onNavigate }) {
  return (
    <li>
      <PageRow to={to} onNavigate={onNavigate}>
        {chip}
        <span className="sheet-row__text">
          <span className="sheet-row__primary">{name}</span>
          {detail && <span className="sheet-row__secondary">{detail}</span>}
        </span>
      </PageRow>
    </li>
  );
}

function Section({ titleKey = null, children }) {
  const { t } = useTranslation();
  return (
    <section className="sheet-section">
      {titleKey && <h2 className="sheet-section__title">{t(titleKey)}</h2>}
      <ul className="sheet-list">{children}</ul>
    </section>
  );
}

export default function MoreSheet({ open, onClose, onOpenContact }) {
  const { t } = useTranslation();
  const { user, userContext, selectedStore, logout } = useAuth();
  const forecastOffered = !forecastsMissing(useInAppSyncStatus());

  const menu = moreMenu({
    isStaff: userContext.user.is_staff,
    forecastOffered,
    store: selectedStore,
  });

  // A link out of the app, so no request of ours sees it: recorded here.
  const handleHelpCenter = () => {
    trackEvent('help_center_opened');
    onClose();
  };

  const handleContact = () => {
    onClose();
    onOpenContact();
  };

  const handleLogout = async () => {
    onClose();
    await logout();
  };

  return (
    <BottomSheet open={open} onClose={onClose} label={t('mobileNav.more')}>
      {menu.pages.map((section) => (
        <Section key={section.links[0].path} titleKey={section.titleKey}>
          <LinkRows links={section.links} onNavigate={onClose} />
        </Section>
      ))}

      <Section titleKey="mobileNav.tools">
        <LinkRows links={menu.tools} onNavigate={onClose} />
      </Section>

      <Section titleKey="mobileNav.account">
        {menu.store && (
          <>
            <IdentityRow
              to={menu.store.profile.path}
              chip={<StoreChip store={selectedStore} />}
              name={storeDisplayName(selectedStore)}
              detail={storeDomainIsDistinct(selectedStore) && selectedStore.domain}
              onNavigate={onClose}
            />
            <LinkRows links={menu.store.links} onNavigate={onClose} />
          </>
        )}
        <IdentityRow
          to={menu.userProfile.path}
          chip={<UserChip user={user} />}
          name={userDisplayName(user)}
          detail={userEmailIsDistinct(user) && user.email}
          onNavigate={onClose}
        />
      </Section>

      <Section titleKey="topMenu.help">
        <li>
          <a
            href={HELP_CENTER_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="sheet-row"
            onClick={handleHelpCenter}
          >
            <span className="sheet-row__text">{t('help.helpCenter')}</span>
          </a>
        </li>
        <li>
          <button type="button" className="sheet-row" onClick={handleContact}>
            <span className="sheet-row__text">{t('contact.title')}</span>
          </button>
        </li>
      </Section>

      <Section>
        <li>
          <button type="button" className="sheet-row" onClick={handleLogout}>
            <span className="sheet-row__text">{t('userDropdown.logout')}</span>
            <span className="sheet-row__trail"><Power size={ICON.lg} /></span>
          </button>
        </li>
      </Section>
    </BottomSheet>
  );
}
